import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Seo, { BREADCRUMB_JSONLD, SERVICE_JSONLD } from "@/components/Seo";

const CLIENT_STEPS = [
  { title: "Submit a design brief", body: "Choose a category, share references, describe your vision. Free quotation." },
  { title: "Curated quotation", body: "Our team reviews scope and delivers a transparent fixed-price quotation with delivery estimate." },
  { title: "Secure payment", body: "Approve the quotation and pay securely. Only after payment does your project begin." },
  { title: "Design Team at work", body: "A verified specialist works under GetJewelDesigns' Design Team. Chat with them anytime through this platform." },
  { title: "Preview & revise", body: "Preview STL/STEP submissions. Two included revisions ensure the final piece matches your vision." },
  { title: "Approve & download", body: "Approve the final work to unlock production-ready files. Your files remain yours, forever." },
];

const DESIGNER_STEPS = [
  { title: "Apply to the studio", body: "Register as a designer and submit your application — experience, software, categories you specialise in, and a portfolio of your best CAD work." },
  { title: "Get approved", body: "Our team reviews every application. Once approved, you unlock the studio dashboard, and can keep your portfolio and payout details up to date any time." },
  { title: "Claim projects", body: "Browse available projects matched to your categories, each with a curated brief, a fixed payout and a deadline. Claim the ones you want — first come, first served." },
  { title: "Design & submit", body: "Work from the design brief, chat with the client through the platform's identity-safe messaging, and submit STL/STEP files for review." },
  { title: "Handle revisions", body: "If the client requests changes within the included revisions, refine and resubmit. Clear scope — new concepts require a separate quotation." },
  { title: "Get paid", body: "When the client approves your work, your earning becomes payable. Payouts are sent to your saved payout method — bank transfer, UPI or PayPal." },
];

const ADMIN_STEPS = [
  { title: "Review incoming briefs", body: "New quotation requests arrive in the console with the client's brief, specifications and reference files. Ask clarifying questions through the quote conversation." },
  { title: "Prepare the quotation", body: "Set the final price, taxes, delivery estimate and the designer payout, and write a client-safe team brief. Defaults come from your Quotation Settings." },
  { title: "Payment creates the job", body: "Once the client pays, the platform automatically creates an order and publishes a job to matching approved designers. No manual dispatch needed." },
  { title: "Curate the design team", body: "Review designer applications and portfolios, approve the best, set concurrent-workload limits, and suspend or reactivate accounts as needed." },
  { title: "Oversee production", body: "Monitor active jobs, deadlines and submissions from the console. The identity firewall keeps clients and designers anonymous to each other." },
  { title: "Settle payouts", body: "Approved work turns into payable earnings. Pay designers through their saved payout method and mark payouts as settled — every action is audit-logged." },
];

const TABS = [
  { key: "client", label: "For Clients", steps: CLIENT_STEPS, heading: "A studio approach, from sketch to STL." },
  { key: "designer", label: "For Designers", steps: DESIGNER_STEPS, heading: "Do the work you love — we handle the rest." },
  { key: "admin", label: "For Studio Admins", steps: ADMIN_STEPS, heading: "One console to run the entire studio." },
];

export default function HowItWorks() {
  const [tab, setTab] = useState("client");
  const active = TABS.find((t) => t.key === tab) || TABS[0];

  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-20">
      <Seo
        title="How It Works"
        description="From brief to production-ready STL: how GetJewelDesigns works for clients and designers. Free quotations, fixed prices, and a managed, confidential process."
        path="/how-it-works"
        jsonLd={[SERVICE_JSONLD, BREADCRUMB_JSONLD([{ name: "Home", path: "/" }, { name: "How it works", path: "/how-it-works" }])]}
      />
      <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Process</div>
      <h1 className="font-serif text-4xl md:text-6xl mb-6">{active.heading}</h1>
      {/* Says what the page covers and where it stops, above the tabs so it is
          the same whichever role is selected. Every fact here is restated from
          the steps and the revision policy below; nothing new is claimed. */}
      <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mb-8 md:mb-10">
        How a GetJewelDesigns commission runs, step by step, for each of the three people involved:
        the client who commissions a design, the specialist designer who models it, and the studio
        admin who quotes and manages it. For a client, that means a free brief, a fixed-price
        quotation, payment, up to two revisions, and approved production-ready files. This page
        covers the process only: prices are on the <Link to="/categories" className="text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors">categories page</Link>, and
        the binding terms are in the <Link to="/terms" className="text-foreground underline underline-offset-4 decoration-primary/50 hover:text-primary transition-colors">Terms &amp; Conditions</Link>.
      </p>

      <div className="flex flex-wrap gap-2 border-b hairline mb-10 md:mb-14" role="tablist" aria-label="How it works by role">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            id={`hiw-tab-${t.key}`}
            aria-controls={`hiw-panel-${t.key}`}
            aria-selected={tab === t.key}
            data-testid={`hiw-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-xs uppercase tracking-widest transition-colors duration-300 border-b-2 -mb-px ${
              tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Every tab's steps are rendered, and the inactive panels are `hidden`.
          Rendering only the active tab left the designer and admin steps out of
          the prerendered HTML, so crawlers and answer engines (which do not run
          JS or click tabs) saw one role's process and nothing else. */}
      {TABS.map((t) => (
      <div key={t.key} role="tabpanel" id={`hiw-panel-${t.key}`} aria-labelledby={`hiw-tab-${t.key}`} hidden={t.key !== active.key}>
      <ol className="space-y-10">
        {t.steps.map((s, i) => (
          <li key={i} className="grid grid-cols-12 gap-4 md:gap-6 border-b hairline pb-10">
            <div className="col-span-3 md:col-span-2">
              <div className="font-serif text-4xl md:text-5xl text-primary">{String(i + 1).padStart(2, "0")}</div>
            </div>
            <div className="col-span-9 md:col-span-10">
              <h2 className="font-serif text-2xl md:text-3xl mb-3">{s.title}</h2>
              <p className="text-muted-foreground max-w-2xl">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>

      {t.key === "client" && (
        <div className="mt-16 border hairline p-8 flex items-start gap-4">
          <CheckCircle2 className="h-5 w-5 text-primary mt-1" strokeWidth={1} />
          <div>
            <div className="font-serif text-2xl mb-2">Revision policy</div>
            <p className="text-sm text-muted-foreground max-w-2xl">Your quotation includes up to 2 revisions unless otherwise specified. Revisions cover reasonable changes to the approved design requirements. New concepts, major scope changes or additional deliverables may require a separate quotation.</p>
          </div>
        </div>
      )}
      {t.key === "designer" && (
        <div className="mt-16 border hairline p-8 flex items-start gap-4">
          <CheckCircle2 className="h-5 w-5 text-primary mt-1" strokeWidth={1} />
          <div>
            <div className="font-serif text-2xl mb-2">Confidentiality & non-circumvention</div>
            <p className="text-sm text-muted-foreground max-w-2xl">All communication and file exchange happens through the platform. Client identities stay protected, and designers agree not to contact or identify clients outside GetJewelDesigns.</p>
          </div>
        </div>
      )}
      </div>
      ))}
    </div>
  );
}
